import { makeAutoObservable, runInAction } from 'mobx';
import { token } from '@/infrastructure/container';
import { type IAdminApi } from '@/services/AdminApi';
import { type ILogger } from '@/services/Logger';
import { type AuditEvent, type EventsQuery } from '@/domain/admin';

/**
 * The audit events-dashboard ViewModel: holds the filter fields and runs the hot+cold query (U1). Verified
 * against `FakeAdminApi`.
 */
export class EventsStore {
  subject = '';
  actor = '';
  action = '';
  clientId = '';
  private _events: AuditEvent[] = [];
  private _loading = false;
  private _error: string | null = null;
  private _ran = false;

  constructor(
    private api: IAdminApi,
    private logger: ILogger,
  ) {
    makeAutoObservable(this);
  }

  get events(): readonly AuditEvent[] {
    return this._events;
  }
  get loading(): boolean {
    return this._loading;
  }
  get error(): string | null {
    return this._error;
  }
  /** True once a query has run (so the view distinguishes "no results" from "not searched yet"). */
  get hasRun(): boolean {
    return this._ran;
  }

  setSubject(v: string): void {
    this.subject = v;
  }
  setActor(v: string): void {
    this.actor = v;
  }
  setAction(v: string): void {
    this.action = v;
  }
  setClientId(v: string): void {
    this.clientId = v;
  }

  async search(): Promise<void> {
    this._loading = true;
    this._error = null;
    const q: EventsQuery = {
      subject: this.subject.trim() || undefined,
      actor: this.actor.trim() || undefined,
      action: this.action.trim() || undefined,
      client_id: this.clientId.trim() || undefined,
      limit: 200,
    };
    try {
      const events = await this.api.queryEvents(q);
      runInAction(() => {
        this._events = events;
        this._ran = true;
      });
    } catch (e) {
      this.logger.error('events query failed', e);
      runInAction(() => (this._error = e instanceof Error ? e.message : String(e)));
    } finally {
      runInAction(() => (this._loading = false));
    }
  }
}

export const TOKEN_EventsStore = token<EventsStore>('EventsStore');
